import React, { useState, useEffect, useCallback } from 'react';
import {
  Undo2, Redo2, History, Clock, Save, RotateCcw,
  Layers, GitBranch,
  XCircle,
  Bookmark, Tag, User
} from 'lucide-react';

export interface HistoryState {
  id: string;
  timestamp: Date;
  action: string;
  description: string;
  flowData: any;
  user?: string;
  tags?: string[];
}

interface FlowHistoryProps {
  currentFlow?: any;
  maxHistorySize?: number;
  onUndo?: () => void;
  onRedo?: () => void;
  onRestore?: (state: HistoryState) => void;
  onSaveCheckpoint?: (name: string, description?: string) => void;
}

export const FlowHistory: React.FC<FlowHistoryProps> = ({
  currentFlow,
  maxHistorySize = 50,
  onUndo,
  onRedo,
  onRestore,
  onSaveCheckpoint
}) => {
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [checkpoints, setCheckpoints] = useState<HistoryState[]>([]);
  const [showCheckpointModal, setShowCheckpointModal] = useState(false);
  const [checkpointName, setCheckpointName] = useState('');
  const [checkpointDescription, setCheckpointDescription] = useState('');
  const [selectedState, setSelectedState] = useState<HistoryState | null>(null);
  const [filter, setFilter] = useState<'all' | 'checkpoints' | 'actions'>('all');

  // Registrar atalhos de teclado
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + Z para desfazer
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl/Cmd + Y ou Ctrl/Cmd + Shift + Z para refazer
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
      // Ctrl/Cmd + S para salvar checkpoint
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        setShowCheckpointModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, history]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addToHistory = useCallback((action: string, description: string, flowData: any) => {
    const newState: HistoryState = {
      id: `history-${Date.now()}`,
      timestamp: new Date(),
      action,
      description,
      flowData: JSON.parse(JSON.stringify(flowData)), // Deep copy
      user: 'Usuário'
    };

    setHistory(prev => {
      const newHistory = prev.slice(0, currentIndex + 1);
      newHistory.push(newState);
      
      // Limitar tamanho do histórico
      if (newHistory.length > maxHistorySize) {
        newHistory.shift();
      }
      
      return newHistory;
    });
    
    setCurrentIndex(prev => Math.min(prev + 1, maxHistorySize - 1));
  }, [currentIndex, maxHistorySize]);

  const handleUndo = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      const previousState = history[currentIndex - 1];
      if (previousState && onRestore) {
        onRestore(previousState);
      }
    } else if (onUndo) {
      onUndo();
    }
  };

  const handleRedo = () => {
    if (currentIndex < history.length - 1) {
      setCurrentIndex(prev => prev + 1);
      const nextState = history[currentIndex + 1];
      if (nextState && onRestore) {
        onRestore(nextState);
      }
    } else if (onRedo) {
      onRedo();
    }
  };

  const handleSaveCheckpoint = () => {
    if (checkpointName && currentFlow) {
      const checkpoint: HistoryState = {
        id: `checkpoint-${Date.now()}`,
        timestamp: new Date(),
        action: 'checkpoint',
        description: checkpointDescription || checkpointName,
        flowData: JSON.parse(JSON.stringify(currentFlow)),
        user: 'Usuário',
        tags: ['checkpoint', checkpointName]
      };
      
      setCheckpoints(prev => [...prev, checkpoint]);
      
      if (onSaveCheckpoint) {
        onSaveCheckpoint(checkpointName, checkpointDescription);
      }
      
      setShowCheckpointModal(false);
      setCheckpointName('');
      setCheckpointDescription('');
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'add_node':
        return <Layers size={12} color="#10B981" />;
      case 'delete_node':
        return <XCircle size={12} color="#EF4444" />;
      case 'connect':
        return <GitBranch size={12} color="#3B82F6" />;
      case 'checkpoint':
        return <Bookmark size={12} color="#F59E0B" />;
      case 'restore':
        return <RotateCcw size={12} color="#8B5CF6" />;
      default:
        return <History size={12} color="#6B7280" />;
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Agora mesmo';
    if (minutes < 60) return `${minutes} min atrás`;
    if (hours < 24) return `${hours}h atrás`;
    return `${days}d atrás`;
  };

  const filteredHistory = filter === 'checkpoints' 
    ? checkpoints 
    : filter === 'actions' 
    ? history 
    : [...history, ...checkpoints].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#ffffff',
      color: '#374151'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #d1d5db',
        background: '#f9fafb'
      }}>
        <h3 style={{
          margin: '0 0 12px 0',
          fontSize: '14px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <History size={16} />
          Histórico e Versionamento
        </h3>

        {/* Undo/Redo Controls */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '12px'
        }}>
          <button
            onClick={handleUndo}
            disabled={currentIndex <= 0}
            style={{
              flex: 1,
              padding: '8px',
              background: currentIndex > 0 ? '#3B82F6' : '#475569',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '12px',
              cursor: currentIndex > 0 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <Undo2 size={14} />
            Desfazer
            <span style={{
              fontSize: '10px',
              background: 'rgba(0, 0, 0, 0.2)',
              padding: '2px 4px',
              borderRadius: '4px'
            }}>
              Ctrl+Z
            </span>
          </button>

          <button
            onClick={handleRedo}
            disabled={currentIndex >= history.length - 1}
            style={{
              flex: 1,
              padding: '8px',
              background: currentIndex < history.length - 1 ? '#10B981' : '#475569',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '12px',
              cursor: currentIndex < history.length - 1 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <Redo2 size={14} />
            Refazer
            <span style={{
              fontSize: '10px',
              background: 'rgba(0, 0, 0, 0.2)',
              padding: '2px 4px',
              borderRadius: '4px'
            }}>
              Ctrl+Y
            </span>
          </button>
        </div>

        <button
          onClick={() => setShowCheckpointModal(true)}
          disabled={!currentFlow}
          style={{
            width: '100%',
            padding: '8px',
            background: currentFlow ? '#8B5CF6' : '#475569',
            border: 'none',
            borderRadius: '6px',
            color: '#ffffff',
            fontSize: '12px',
            cursor: currentFlow ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          <Save size={14} />
          Salvar Checkpoint
          <span style={{
            fontSize: '10px',
            background: 'rgba(0, 0, 0, 0.2)',
            padding: '2px 4px',
            borderRadius: '4px'
          }}>
            Ctrl+S
          </span>
        </button>
      </div>

      {/* Filter Tabs */}
      <div style={{
        padding: '12px',
        borderBottom: '1px solid #d1d5db',
        display: 'flex',
        gap: '8px'
      }}>
        {(['all', 'actions', 'checkpoints'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              flex: 1,
              padding: '6px',
              background: filter === f ? '#3B82F6' : '#f1f5f9',
              border: `1px solid ${filter === f ? '#3B82F6' : '#d1d5db'}`,
              borderRadius: '6px',
              color: filter === f ? '#ffffff' : '#374151',
              fontSize: '11px',
              cursor: 'pointer'
            }}
          >
            {f === 'all' ? 'Todos' : f === 'actions' ? 'Ações' : 'Checkpoints'}
            {f === 'actions' && ` (${history.length})`}
            {f === 'checkpoints' && ` (${checkpoints.length})`}
          </button>
        ))}
      </div>

      {/* History List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px'
      }}>
        {filteredHistory.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '200px',
            color: '#64748b'
          }}>
            <Clock size={40} style={{ marginBottom: '12px' }} />
            <p style={{ margin: 0, fontSize: '12px' }}>
              Nenhum histórico disponível
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredHistory.map((state, index) => (
              <div
                key={state.id}
                onClick={() => setSelectedState(state)}
                style={{
                  padding: '10px',
                  background: selectedState?.id === state.id ? '#f1f5f9' : '#ffffff',
                  border: `1px solid ${selectedState?.id === state.id ? '#3B82F6' : '#e5e7eb'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#3B82F6'}
                onMouseLeave={e => e.currentTarget.style.borderColor = selectedState?.id === state.id ? '#3B82F6' : '#e5e7eb'}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '4px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {getActionIcon(state.action)}
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: state.action === 'checkpoint' ? '#F59E0B' : '#e2e8f0'
                    }}>
                      {state.description}
                    </span>
                  </div>
                  <span style={{
                    fontSize: '10px',
                    color: '#64748b'
                  }}>
                    {formatTime(state.timestamp)}
                  </span>
                </div>

                {state.tags && state.tags.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: '4px',
                    marginTop: '4px'
                  }}>
                    {state.tags.map(tag => (
                      <span
                        key={tag}
                        style={{
                          fontSize: '9px',
                          color: '#6b7280',
                          background: '#ffffff',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px'
                        }}
                      >
                        <Tag size={8} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {state.user && (
                  <div style={{
                    marginTop: '4px',
                    fontSize: '10px',
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <User size={10} />
                    {state.user}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected State Actions */}
      {selectedState && (
        <div style={{
          padding: '12px',
          borderTop: '1px solid #d1d5db',
          background: '#f9fafb'
        }}>
          <div style={{
            marginBottom: '8px',
            fontSize: '11px',
            color: '#6b7280'
          }}>
            Selecionado: {selectedState.description}
          </div>
          <button
            onClick={() => {
              if (onRestore) {
                onRestore(selectedState);
                setSelectedState(null);
              }
            }}
            style={{
              width: '100%',
              padding: '8px',
              background: '#3B82F6',
              border: 'none',
              borderRadius: '6px',
              color: '#ffffff',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <RotateCcw size={14} />
            Restaurar Este Estado
          </button>
        </div>
      )}

      {/* Checkpoint Modal */}
      {showCheckpointModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: '400px',
            maxWidth: '90%',
            background: '#f9fafb',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '16px',
              fontWeight: 600,
              color: '#374151'
            }}>
              Salvar Checkpoint
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '4px'
              }}>
                Nome do Checkpoint *
              </label>
              <input
                type="text"
                value={checkpointName}
                onChange={(e) => setCheckpointName(e.target.value)}
                placeholder="Ex: Versão estável"
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#374151',
                  fontSize: '12px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '4px'
              }}>
                Descrição (opcional)
              </label>
              <textarea
                value={checkpointDescription}
                onChange={(e) => setCheckpointDescription(e.target.value)}
                placeholder="Descreva as mudanças importantes..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  color: '#374151',
                  fontSize: '12px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowCheckpointModal(false);
                  setCheckpointName('');
                  setCheckpointDescription('');
                }}
                style={{
                  padding: '8px 16px',
                  background: '#6B7280',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveCheckpoint}
                disabled={!checkpointName}
                style={{
                  padding: '8px 16px',
                  background: checkpointName ? '#3B82F6' : '#475569',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ffffff',
                  fontSize: '12px',
                  cursor: checkpointName ? 'pointer' : 'not-allowed'
                }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Info */}
      <div style={{
        padding: '12px',
        background: '#f9fafb',
        borderTop: '1px solid #d1d5db'
      }}>
        <h4 style={{
          margin: '0 0 8px 0',
          fontSize: '11px',
          fontWeight: 600,
          color: '#6b7280'
        }}>
          Atalhos de Teclado
        </h4>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '4px',
          fontSize: '10px',
          color: '#64748b'
        }}>
          <div>
            <kbd style={{
              padding: '2px 4px',
              background: '#ffffff',
              borderRadius: '2px',
              marginRight: '4px'
            }}>Ctrl+Z</kbd>
            Desfazer
          </div>
          <div>
            <kbd style={{
              padding: '2px 4px',
              background: '#ffffff',
              borderRadius: '2px',
              marginRight: '4px'
            }}>Ctrl+Y</kbd>
            Refazer
          </div>
          <div>
            <kbd style={{
              padding: '2px 4px',
              background: '#ffffff',
              borderRadius: '2px',
              marginRight: '4px'
            }}>Ctrl+S</kbd>
            Salvar Checkpoint
          </div>
          <div>
            <kbd style={{
              padding: '2px 4px',
              background: '#ffffff',
              borderRadius: '2px',
              marginRight: '4px'
            }}>Delete</kbd>
            Deletar Seleção
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowHistory;
